import { FC } from 'react';
import { Box, LinearProgress, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

/**
 * Maturity score as a coloured bar plus its percentage, the way the design's
 * remediation table shows it.
 *
 * The colour bands mirror `features/dashboard/score-band.ts`' thresholds so a
 * score reads the same here as it does on the dashboard.
 */
export const ScoreBar: FC<{ score: number | null }> = observer(({ score }) => {
  const theme = useTheme();

  if (score == null) {
    return (
      <Typography variant="textSM_regular" sx={{ color: 'text.tertiary' }}>
        —
      </Typography>
    );
  }

  const value = Math.max(0, Math.min(100, Math.round(score)));
  const color =
    value >= 80
      ? theme.palette.green[700]
      : value >= 60
        ? theme.palette.blue[700]
        : value >= 40
          ? theme.palette.warning.main
          : theme.palette.error.main;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 110 }}>
      <LinearProgress
        variant="determinate"
        value={value}
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 999,
          '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 999 },
        }}
      />
      <Typography sx={{ fontSize: 13, fontWeight: 600, color, minWidth: 34 }}>
        {value}%
      </Typography>
    </Box>
  );
});
